import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { fetchUser } from './store/Slices/userSlice';
import { lightTheme as lightThemeBinance, darkTheme as darkThemeBinance } from './themes/themes';
import { lightTheme as lightThemeWelcome, darkTheme as darkThemeWelcome } from './themes/themes-welcome';
import Welcome from './Pages/Welcome/Welcome';
import Register from './Pages/Register/Register';
import Login from './Pages/Register/Login';
import Home from './Pages/Home';
import Spot from './Pages/Spot';
import Futures from './Pages/Futures/Futures';
import AiTrading from './Pages/AiTrading/AiTrading';
import Assets from './Pages/Assets';
import Profile from './Pages/Profile';
import ProtectedRoute from './Components/ProtectedRoute';
import CreateSupportRequest from './Pages/Support/CreateSupportRequest';
import SupportRequest from './Pages/Support/SupportRequest';
import Support from './Pages/Support/Support';
import StaffSupportDashboard from './Pages/Support/StaffSupportDashboard';
import CannedResponseManager from './Pages/Support/CannedResponseManager';
import ChatSessionPage from './Pages/Support/ChatSessionPage';
import TransferSection from './Pages/Transfer/TransferSection';
import LuckWheel from './Pages/Earn/LuckWheel';
import NotificationsPage from './Components/NavComponents/Notification';
import ReferralPage from './Components/SideNav/Sections/Referral';
import { initializeSocket, disconnectSocket } from './services/socket';
import { incrementUnreadMessages } from './store/Slices/supportSlice';
import { 
  Snackbar, Box, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, Button, Typography, useMediaQuery, Backdrop 
} from '@mui/material'; // Added Backdrop
import { useTheme } from '@mui/material/styles';
// Import professional warning icons
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';

import DepositPage from './Pages/Assets/DepositPage';
import WithdrawPage from './Pages/Assets/WithdrawPage';
import WithdrawRequests from './Pages/Support/WithdrawRequests';
import WithdrawHistory from './Pages/Assets/WithdrawHistory';
import DepositRecordsPage from './Pages/Assets/DepositRecordsPage';
import VerifyPage from './Pages/Verify/VerifyPage';
import KycQueue from './Pages/Support/KYC/KycQueue';
import PromotionReviewPage from './Pages/PromoteEarn/PromotionReviewPage';
import PromoteEarnPage from './Pages/PromoteEarn/PromoteEarnPage';
import PromotionLeaderboardPage from './Pages/PromoteEarn/PromotionLeaderboardPage';
import AboutUs from './Pages/Welcome/AboutUs';
import HelpCenter from './Pages/Welcome/HelpCenter';
import SystemStatus from './Pages/Welcome/SystemStatus';
import Fees from './Pages/Welcome/Fees';
import Careers from './Pages/Welcome/Careers';
import PrivacyPolicy from './Pages/Welcome/PrivacyPolicy';
import Terms from './Pages/Welcome/Terms';
import BlogHome from './Pages/Welcome/Blog/BlogHome';
import BlogArticle from './Pages/Welcome/Blog/BlogArticle';
import Security from './Pages/Welcome/Security';
import Disclosure from './Pages/Welcome/Disclosure';
import Markets from './Pages/Welcome/market/Markets';
import GiftCode from './Components/SideNav/Sections/GiftCode';

// --- START: Refined Professional Warning Messages ---
const warningMessages = [
  {
    lang: "EN",
    label: "Critical Disclaimer",
    text: "This platform is a DEMONSTRATION SIMULATOR for engineering purposes only. It is NOT a licensed exchange. DO NOT deposit real assets. Any funds transferred to this system are irreversible and effectively lost."
  },
  {
    lang: "AR",
    label: "تحذير أمني هام",
    text: "هذه المنصة عبارة عن بيئة محاكاة برمجية فقط وليست منصة تداول مرخصة. يُحظر إيداع أموال حقيقية أو استخدامها في معاملات مالية فعلية. أي أصول يتم تحويلها هنا تعتبر مفقودة وغير قابلة للاسترداد."
  },
  {
    lang: "ES",
    label: "Advertencia Crítica",
    text: "Esta plataforma es un SIMULADOR DE DEMOSTRACIÓN. NO es un intercambio con licencia. NO deposite activos reales. Los fondos transferidos aquí son irreversibles."
  },
  {
    lang: "FR",
    label: "Avertissement Critique",
    text: "Cette plateforme est un SIMULATEUR. Ce n'est PAS un échange agréé. NE déposez PAS d'actifs réels. Tout fonds transféré vers ce système est irréversiblement perdu."
  },
  {
    lang: "RU",
    label: "Критическое предупреждение",
    text: "Эта платформа является СИМУЛЯТОРОМ. Это НЕ лицензированная биржа. НЕ вносите реальные средства. Любые переведенные средства считаются безвозвратно утерянными."
  }
];
// --- END: Refined Professional Warning Messages ---

const NotificationWrapper = ({ children, snackbarOpen, snackbarMessage, handleSnackbarClose }) => {
  const theme = useTheme();

  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        key={snackbarMessage + Date.now()}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{
            bgcolor: theme.palette.brand.main,
            color: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            fontWeight: 'medium',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// --- START: High-End Professional Disclaimer Modal ---
const DisclaimerModal = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Changed key to 'v2' to force users to see the new professional design
    const hasAccepted = localStorage.getItem('disclaimer_accepted_professional_v2');
    if (!hasAccepted) {
      setOpen(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('disclaimer_accepted_professional_v2', 'true');
    setOpen(false);
  };

  // Professional Dark/Red Palette (Cyber-Security Style)
  const colors = {
    bg: '#0F0F0F',
    surface: '#1A1A1A',
    primaryRed: '#FF3B30',
    accentGradient: 'linear-gradient(135deg, #FF3B30 0%, #FF9500 100%)',
    textMain: '#FFFFFF',
    textMuted: '#9e9e9e',
    border: 'rgba(255, 59, 48, 0.3)'
  };

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      disableEscapeKeyDown
      onClose={(e, r) => r !== 'backdropClick' && {}}
      // Adds a glassmorphism blur to the background
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
          },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: colors.bg,
          backgroundImage: 'none',
          color: colors.textMain,
          maxWidth: '600px',
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          boxShadow: '0 0 50px rgba(255, 59, 48, 0.15)',
          overflow: 'hidden',
          position: 'relative'
        }
      }}
    >
      {/* Decorative Top Line */}
      <Box sx={{ height: '4px', background: colors.accentGradient, width: '100%' }} />

      <DialogTitle sx={{ 
        textAlign: 'center', 
        pt: 5, 
        pb: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2 
      }}>
        {/* Animated Icon Container */}
        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 59, 48, 0.1)',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(255, 59, 48, 0.4)' },
            '70%': { boxShadow: '0 0 0 15px rgba(255, 59, 48, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(255, 59, 48, 0)' },
          }
        }}>
           <GppBadOutlinedIcon sx={{ fontSize: 45, color: colors.primaryRed }} />
        </Box>

        <Box>
          <Typography variant="h5" sx={{ 
            fontWeight: 800, 
            letterSpacing: '1px', 
            textTransform: 'uppercase',
            background: colors.accentGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Simulation Environment
          </Typography>
          <Typography variant="subtitle2" sx={{ color: colors.textMuted, mt: 0.5, letterSpacing: '2px', fontSize: '0.75rem' }}>
            NON-OPERATIONAL TRADING SYSTEM
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: fullScreen ? 3 : 5, py: 2 }}>
        {/* Main Alert Box */}
        <Box sx={{ 
          backgroundColor: 'rgba(255, 59, 48, 0.08)', 
          border: '1px solid rgba(255, 59, 48, 0.2)', 
          borderRadius: '8px', 
          p: 2, 
          mb: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}>
          <WarningAmberRoundedIcon sx={{ color: colors.primaryRed, mt: 0.3 }} />
          <Typography variant="body2" sx={{ color: '#ffcdd2', lineHeight: 1.6, fontSize: '0.9rem' }}>
            <strong>STOP & READ:</strong> This application is a portfolio project designed to demonstrate full-stack engineering capabilities. It is not connected to any real banking system or blockchain.
          </Typography>
        </Box>

        {/* Scrollable Languages Section */}
        <Box sx={{ 
          maxHeight: '220px', 
          overflowY: 'auto', 
          pr: 1,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#1a1a1a' },
          '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '4px' }
        }}>
          {warningMessages.map((msg, index) => (
            <Box key={index} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ 
                  bgcolor: '#333', 
                  color: '#fff', 
                  px: 0.8, 
                  py: 0.2, 
                  borderRadius: '4px', 
                  fontSize: '0.65rem',
                  fontWeight: 'bold'
                }}>
                  {msg.lang}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: colors.primaryRed, fontSize: '0.8rem', fontWeight: 700 }}>
                  {msg.label}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc', fontSize: '0.85rem', lineHeight: 1.5, pl: 0.5, borderLeft: '2px solid #333' }}>
                {msg.text}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Developer Credits (Subtle) */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #333', textAlign: 'center' }}>
           <Typography variant="caption" sx={{ color: '#555', fontFamily: 'monospace' }}>
             Eng. Hassan Youzghadli & Eng. Yahia Dareer
           </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: fullScreen ? 3 : 4, pt: 1, flexDirection: 'column', gap: 1 }}>
        <Button 
          onClick={handleAccept} 
          variant="contained" 
          fullWidth
          sx={{ 
            background: colors.primaryRed,
            color: '#fff',
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '0.95rem',
            boxShadow: '0 8px 20px rgba(255, 59, 48, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: '#d50000',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 25px rgba(255, 59, 48, 0.5)',
            }
          }}
        >
          I UNDERSTAND THE RISKS / أقر بفهم المخاطر
        </Button>
        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', textAlign: 'center', width: '100%' }}>
          By clicking above, you acknowledge that no financial claims can be made against the developers.
        </Typography>
      </DialogActions>
    </Dialog>
  );
};
// --- END: High-End Professional Disclaimer Modal ---

function App() {
  const dispatch = useDispatch();
  const isLightTheme = useSelector((state) => state.theme.light);
  const user = useSelector((state) => state.user.data);
  const userLoading = useSelector((state) => state.user.loading);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !user && !userLoading) {
      dispatch(fetchUser());
    }
  }, [dispatch, user, userLoading]);

  useEffect(() => {
    if (user?._id) {
      initializeSocket(dispatch, user._id);
    }

    return () => {
      disconnectSocket();
    };
  }, [dispatch, user?._id]);

  useEffect(() => {
    const handleNewSupportMessage = ({ support_session_id, message, sender_id }) => {
      setSnackbarMessage(`New support message: ${message}`);
      setSnackbarOpen(false);
      setTimeout(() => setSnackbarOpen(true), 10);
      dispatch(incrementUnreadMessages());
    };

    const handleTradeOpened = (trade) => {
      setSnackbarMessage(`New trade opened: ${trade._id}`);
      setSnackbarOpen(false);
      setTimeout(() => setSnackbarOpen(true), 10);
    };

    const handleTradeClosed = ({ tradeId, profit_loss }) => {
      setSnackbarMessage(`Trade ${tradeId} closed: ${profit_loss >= 0 ? 'Profit' : 'Loss'} $${Math.abs(profit_loss).toFixed(2)}`);
      setSnackbarOpen(false);
      setTimeout(() => setSnackbarOpen(true), 10);
    };

    if (user?._id) {
      const socket = initializeSocket(dispatch, user._id);
      socket.on('new_support_message', handleNewSupportMessage);
      socket.on('trade_opened', handleTradeOpened);
      socket.on('trade_closed', handleTradeClosed);
    }

    return () => {
      disconnectSocket();
    };
  }, [dispatch, user?._id]);

  const withWelcomeTheme = (Component) => (
    <ThemeProvider theme={isLightTheme ? lightThemeWelcome : darkThemeWelcome}>
      <Component />
    </ThemeProvider>
  );

  const withBinanceTheme = (Component) => (
    <ThemeProvider theme={isLightTheme ? lightThemeBinance : darkThemeBinance}>
      <NotificationWrapper
        snackbarOpen={snackbarOpen}
        snackbarMessage={snackbarMessage}
        handleSnackbarClose={() => setSnackbarOpen(false)}
      >
        <Component />
      </NotificationWrapper>
    </ThemeProvider>
  );

  return (
    <Router>
      <DisclaimerModal /> {/* Professional Disclaimer Modal */}
      <PersistGate loading={null} persistor={persistor}>
        <Routes>
          <Route path="/" element={withWelcomeTheme(Welcome)} />
          <Route path="/about-us" element={withWelcomeTheme(AboutUs)} />
          <Route path="/help-center" element={withWelcomeTheme(HelpCenter)} />
          <Route path="/privacy-policy" element={withWelcomeTheme(PrivacyPolicy)} />
          <Route path="/status" element={withWelcomeTheme(SystemStatus)} />
          <Route path="/fees" element={withWelcomeTheme(Fees)} />
          <Route path="/privacy" element={withWelcomeTheme(PrivacyPolicy)} />
          <Route path="/terms" element={withWelcomeTheme(Terms)}/>
          <Route path="/security" element={withWelcomeTheme(Security)} />
          <Route path="/disclosure" element={withWelcomeTheme(Disclosure)} />
          <Route path="/markets" element={withWelcomeTheme(Markets)} />
          <Route path="/careers" element={withWelcomeTheme(Careers)} />
          <Route path="/blog" element={withWelcomeTheme(BlogHome)} />
          <Route path="/blog/:slug" element={withWelcomeTheme(BlogArticle)} />
          <Route path="/register" element={withWelcomeTheme(Register)} />
          <Route path="/login" element={withWelcomeTheme(Login)} />


          <Route path="/home" element={<ProtectedRoute>{withBinanceTheme(Home)}</ProtectedRoute>} />
          <Route path="/spot" element={<ProtectedRoute>{withBinanceTheme(Spot)}</ProtectedRoute>} />
          <Route path="/futures" element={<ProtectedRoute>{withBinanceTheme(Futures)}</ProtectedRoute>} />
          <Route path="/ai-trading" element={<ProtectedRoute>{withBinanceTheme(AiTrading)}</ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute>{withBinanceTheme(Assets)}</ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute>{withBinanceTheme(Profile)}</ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute>{withBinanceTheme(NotificationsPage)}</ProtectedRoute> }/>
          <Route path="/Referral" element={<ProtectedRoute>{withBinanceTheme(ReferralPage)}</ProtectedRoute> }/>
          <Route path='/C-Box' element={<ProtectedRoute>{withBinanceTheme(GiftCode)}</ProtectedRoute> }/>


          <Route path="/support/create" element={<ProtectedRoute>{withBinanceTheme(CreateSupportRequest)}</ProtectedRoute>} />
          <Route path="/support/requests" element={<ProtectedRoute>{withBinanceTheme(SupportRequest)}</ProtectedRoute>} />
          <Route path="/support/sessions" element={<ProtectedRoute>{withBinanceTheme(Support)}</ProtectedRoute>} />
          <Route path="/support/session/:sessionId" element={<ProtectedRoute>{withBinanceTheme(ChatSessionPage)}</ProtectedRoute>} />
          <Route path="/staff/support-dashboard" element={<ProtectedRoute staffOnly>{withBinanceTheme(StaffSupportDashboard)}</ProtectedRoute>} />
          <Route path="/staff/canned-responses" element={<ProtectedRoute staffOnly>{withBinanceTheme(CannedResponseManager)}</ProtectedRoute>} />
          <Route path="/Transfer" element={<ProtectedRoute >{withBinanceTheme(TransferSection)}</ProtectedRoute>} />
          <Route path="/luck-wheel" element={<ProtectedRoute>{withBinanceTheme(LuckWheel)}</ProtectedRoute>} /> {/* مسار دولاب الحظ */}
          <Route path="/Deposit" element={<ProtectedRoute>{withBinanceTheme(DepositPage)}</ProtectedRoute>} />
          <Route path="/deposit/records" element={<ProtectedRoute>{withBinanceTheme(DepositRecordsPage)}</ProtectedRoute>} />
          <Route path="*" element={withWelcomeTheme(Welcome)} />
          <Route path='/verify' element={<ProtectedRoute>{withBinanceTheme(VerifyPage)}</ProtectedRoute>} />
          <Route path="/withdraw" element={<ProtectedRoute>{withBinanceTheme(WithdrawPage)}</ProtectedRoute>} />
          <Route path="/withdraw/history" element={<ProtectedRoute>{withBinanceTheme(WithdrawHistory)}</ProtectedRoute>} />
          <Route path="/staff/withdraw-requests" element={<ProtectedRoute staffOnly>{withBinanceTheme(WithdrawRequests)}</ProtectedRoute>} />
          <Route path="/support/kyc" element={<ProtectedRoute staffOnly>{withBinanceTheme(KycQueue)}</ProtectedRoute>} />
          <Route path="/promote-earn" element={<ProtectedRoute>{withBinanceTheme(PromoteEarnPage)}</ProtectedRoute>} />
          <Route path="/promote-earn/review" element={<ProtectedRoute staffOnly>{withBinanceTheme(PromotionReviewPage)}</ProtectedRoute>} />
          <Route path="/promotions/leaderboard"element={<ProtectedRoute>{withBinanceTheme(PromotionLeaderboardPage)}</ProtectedRoute>}/>
        </Routes>
      </PersistGate>
    </Router>
  );
}

export default App;